import { Badge } from '@/components/ui/badge'
import { formatConditionCategory } from '@/lib/format'
import type { IntakeResponse } from '@/types/visit'

function getStr(answers: Record<string, unknown>, key: string): string {
  const val = answers[key]
  return typeof val === 'string' ? val : ''
}

function getNum(answers: Record<string, unknown>, key: string): number | null {
  const val = answers[key]
  return typeof val === 'number' ? val : null
}

function getStrArray(answers: Record<string, unknown>, key: string): string[] {
  const val = answers[key]
  return Array.isArray(val) ? val.filter((v): v is string => typeof v === 'string') : []
}

function formatLabel(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function ScaleBar({ value, max = 10 }: { value: number; max?: number }) {
  const percentage = (value / max) * 100
  let colorClass = 'bg-green-500'
  if (value > 6) colorClass = 'bg-red-500'
  else if (value > 3) colorClass = 'bg-yellow-500'

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-full bg-slate-100">
        <div
          className={`h-2 rounded-full ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm font-medium text-slate-700">{value}/{max}</span>
    </div>
  )
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  )
}

function AcneLayout({ answers }: { answers: Record<string, unknown> }) {
  const affectedAreas = getStrArray(answers, 'affected_areas')
  const triggers = getStrArray(answers, 'breakout_triggers')

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
      {/* Row 1: severity + duration */}
      {getStr(answers, 'severity') && (
        <FieldRow label="Severity">{formatLabel(getStr(answers, 'severity'))}</FieldRow>
      )}
      {getStr(answers, 'duration') && (
        <FieldRow label="Duration">{getStr(answers, 'duration')}</FieldRow>
      )}
      {/* Row 2: scarring + hormonal */}
      {getStr(answers, 'scarring_level') && (
        <FieldRow label="Scarring Level">{formatLabel(getStr(answers, 'scarring_level'))}</FieldRow>
      )}
      {getStr(answers, 'hormonal_factors') && (
        <FieldRow label="Hormonal Factors">{getStr(answers, 'hormonal_factors')}</FieldRow>
      )}
      {/* Row 3: affected areas (full width) */}
      {affectedAreas.length > 0 && (
        <div className="col-span-2">
          <FieldRow label="Affected Areas">
            <div className="flex flex-wrap gap-1.5">
              {affectedAreas.map((area) => (
                <Badge key={area} variant="secondary">{formatLabel(area)}</Badge>
              ))}
            </div>
          </FieldRow>
        </div>
      )}
      {/* Row 4: breakout triggers (full width) */}
      {triggers.length > 0 && (
        <div className="col-span-2">
          <FieldRow label="Breakout Triggers">
            <div className="flex flex-wrap gap-1.5">
              {triggers.map((trigger) => (
                <Badge key={trigger} variant="secondary">{formatLabel(trigger)}</Badge>
              ))}
            </div>
          </FieldRow>
        </div>
      )}
      {/* Row 5: previous treatments + skincare */}
      {getStr(answers, 'previous_treatments') && (
        <FieldRow label="Previous Treatments">{getStr(answers, 'previous_treatments')}</FieldRow>
      )}
      {getStr(answers, 'skincare_routine') && (
        <FieldRow label="Skincare Routine">{getStr(answers, 'skincare_routine')}</FieldRow>
      )}
      {/* Row 6: notes (full width) */}
      {getStr(answers, 'notes') && (
        <div className="col-span-2">
          <FieldRow label="Notes">{getStr(answers, 'notes')}</FieldRow>
        </div>
      )}
    </div>
  )
}

function GeneralLayout({ answers }: { answers: Record<string, unknown> }) {
  const symptoms = getStrArray(answers, 'symptoms')
  const painLevel = getNum(answers, 'pain_level')
  const itchingLevel = getNum(answers, 'itching_level')

  return (
    <div className="grid grid-cols-2 gap-x-6">
      {/* Left column */}
      <div className="space-y-4">
        {getStr(answers, 'chief_complaint') && (
          <FieldRow label="Chief Complaint">
            <p className="font-medium">{getStr(answers, 'chief_complaint')}</p>
          </FieldRow>
        )}
        {getStr(answers, 'duration') && (
          <FieldRow label="Duration">{getStr(answers, 'duration')}</FieldRow>
        )}
        {getStr(answers, 'trajectory') && (
          <FieldRow label="Trajectory">{getStr(answers, 'trajectory')}</FieldRow>
        )}
        {getStr(answers, 'previous_treatments') && (
          <FieldRow label="Previous Treatments">{getStr(answers, 'previous_treatments')}</FieldRow>
        )}
        {getStr(answers, 'notes') && (
          <FieldRow label="Notes">{getStr(answers, 'notes')}</FieldRow>
        )}
      </div>
      {/* Right column */}
      <div className="space-y-4">
        {symptoms.length > 0 && (
          <FieldRow label="Symptoms">
            <div className="flex flex-wrap gap-1.5">
              {symptoms.map((symptom) => (
                <Badge key={symptom} variant="secondary">{formatLabel(symptom)}</Badge>
              ))}
            </div>
          </FieldRow>
        )}
        {painLevel !== null && (
          <FieldRow label="Pain Level">
            <ScaleBar value={painLevel} />
          </FieldRow>
        )}
        {itchingLevel !== null && (
          <FieldRow label="Itching Level">
            <ScaleBar value={itchingLevel} />
          </FieldRow>
        )}
      </div>
    </div>
  )
}

interface IntakePanelProps {
  intake: IntakeResponse | null
  title?: string
}

export default function IntakePanel({ intake, title = 'Intake' }: IntakePanelProps) {
  if (!intake) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <h2 className="text-lg font-medium text-slate-900">{title}</h2>
        <p className="mt-4 text-center text-sm text-slate-500">
          No intake form submitted yet.
        </p>
      </div>
    )
  }

  const isAcne = intake.condition_category === 'acne'

  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-lg font-medium text-slate-900">{title}</h2>
        <Badge>{formatConditionCategory(intake.condition_category)}</Badge>
      </div>
      {isAcne ? (
        <AcneLayout answers={intake.answers} />
      ) : (
        <GeneralLayout answers={intake.answers} />
      )}
    </div>
  )
}
